import { useState, useEffect } from 'react';
import { api, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Truck, Loader2, ChevronLeft, ChevronRight, Clock, CheckCircle2,
  Package, CreditCard, Eye, AlertCircle,
} from 'lucide-react';

const STATUS_CONFIG = {
  pending_payment: { label: 'Pago Pendiente', color: 'text-amber-400 bg-amber-500/10', icon: CreditCard },
  payment_uploaded: { label: 'Pago Subido', color: 'text-blue-400 bg-blue-500/10', icon: AlertCircle },
  payment_confirmed: { label: 'Pago Confirmado', color: 'text-cyan-400 bg-cyan-500/10', icon: CheckCircle2 },
  assigned: { label: 'Asignado', color: 'text-indigo-400 bg-indigo-500/10', icon: Package },
  in_transit: { label: 'En Transito', color: 'text-purple-400 bg-purple-500/10', icon: Truck },
  delivered: { label: 'Entregado', color: 'text-emerald-400 bg-emerald-500/10', icon: CheckCircle2 },
  canceled: { label: 'Cancelado', color: 'text-red-400 bg-red-500/10', icon: AlertCircle },
};

export default function AdminEnbixosPage() {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailDialog, setDetailDialog] = useState(null);

  useEffect(() => { load(); }, [page, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const data = await api.adminListEnbixos(params);
      setRequests(data.requests || []);
      setTotal(data.total || 0);
      setStats(data.stats || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const fmt = (v) => v ? new Date(v).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';
  const fmtMoney = (v) => v ? `$${Number(v).toLocaleString('es-MX')} MXN` : '-';

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="admin-enbixos-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Enbixos - Logistica</h1>
          <p className="text-xs text-zinc-600">{total} solicitudes en total</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Pago Pendiente', value: stats.pending_payment || 0, icon: CreditCard, color: 'text-amber-400' },
          { label: 'Pago Subido', value: stats.payment_uploaded || 0, icon: AlertCircle, color: 'text-blue-400' },
          { label: 'En Transito', value: stats.in_transit || 0, icon: Truck, color: 'text-purple-400' },
          { label: 'Entregados', value: stats.delivered || 0, icon: CheckCircle2, color: 'text-emerald-400' },
        ].map(s => (
          <div key={s.label} className="p-4 rounded-xl border border-white/[0.04] bg-[#0A0A0D]">
            <s.icon className={`w-4 h-4 ${s.color} mb-2`} />
            <p className="text-2xl font-bold text-zinc-100">{s.value}</p>
            <p className="text-[10px] text-zinc-600 uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-3 mb-6">
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-48 bg-white/[0.03] border-white/5 text-zinc-300 h-9 text-sm" data-testid="enbixos-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#121215] border-white/10">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending_payment">Pago Pendiente</SelectItem>
            <SelectItem value="payment_uploaded">Pago Subido</SelectItem>
            <SelectItem value="payment_confirmed">Pago Confirmado</SelectItem>
            <SelectItem value="assigned">Asignado</SelectItem>
            <SelectItem value="in_transit">En Transito</SelectItem>
            <SelectItem value="delivered">Entregado</SelectItem>
            <SelectItem value="canceled">Cancelado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.04]">
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">ID</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Comprador</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Vendedor</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Ruta</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Costo</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Status</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Fecha</th>
                <th className="text-right px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-12"><Loader2 className="w-5 h-5 text-zinc-600 animate-spin mx-auto" /></td></tr>
              ) : requests.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-zinc-600 text-sm">Sin solicitudes</td></tr>
              ) : requests.map(r => {
                const sc = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending_payment;
                return (
                  <tr key={r.id} className="border-b border-white/[0.02] hover:bg-white/[0.01]" data-testid={`enbixos-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <span className="text-zinc-400 text-[10px] font-mono">{r.id?.slice(0, 8)}</span>
                    </td>
                    <td className="px-4 py-3"><span className="text-zinc-200 text-xs">{r.buyer_name || '-'}</span></td>
                    <td className="px-4 py-3"><span className="text-zinc-200 text-xs">{r.seller_name || '-'}</span></td>
                    <td className="px-4 py-3">
                      <span className="text-zinc-400 text-[10px]">{r.origin_city || '?'} &rarr; {r.destination_city || '?'}</span>
                    </td>
                    <td className="px-4 py-3"><span className="text-zinc-100 text-xs">{fmtMoney(r.total_cost || r.quoted_cost)}</span></td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${sc.color}`}>{sc.label}</span>
                    </td>
                    <td className="px-4 py-3"><span className="text-[10px] text-zinc-600">{fmt(r.created_at)}</span></td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setDetailDialog(r)}
                        className="h-7 text-[10px] text-zinc-300 hover:bg-white/5" data-testid={`view-enbixos-${r.id}`}>
                        <Eye className="w-3 h-3 mr-1" /> Ver
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.04]">
          <span className="text-xs text-zinc-600">Pagina {page} de {Math.ceil(total / 20) || 1}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs text-zinc-400">
              <ChevronLeft className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="ghost" disabled={requests.length < 20} onClick={() => setPage(p => p + 1)} className="h-7 text-xs text-zinc-400">
              <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>

      {/* Detail Dialog */}
      {detailDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setDetailDialog(null)}>
          <div className="bg-[#121215] border border-white/10 rounded-xl max-w-lg w-full mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="enbixos-detail-dialog">
            <h3 className="text-lg font-semibold text-zinc-100 mb-4">Detalle de Envio</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-[10px] text-zinc-600 block">ID</span><span className="text-zinc-300 font-mono text-xs">{detailDialog.id?.slice(0, 12)}</span></div>
              <div><span className="text-[10px] text-zinc-600 block">Status</span><span className={`text-[10px] px-2 py-0.5 rounded-full ${(STATUS_CONFIG[detailDialog.status] || {}).color}`}>{(STATUS_CONFIG[detailDialog.status] || {}).label}</span></div>
              <div><span className="text-[10px] text-zinc-600 block">Comprador</span><span className="text-zinc-200">{detailDialog.buyer_name}</span></div>
              <div><span className="text-[10px] text-zinc-600 block">Vendedor</span><span className="text-zinc-200">{detailDialog.seller_name}</span></div>
              <div><span className="text-[10px] text-zinc-600 block">Origen</span><span className="text-zinc-200">{detailDialog.origin_city}, {detailDialog.origin_state}</span></div>
              <div><span className="text-[10px] text-zinc-600 block">Destino</span><span className="text-zinc-200">{detailDialog.destination_city}, {detailDialog.destination_state}</span></div>
              <div><span className="text-[10px] text-zinc-600 block">Especie</span><span className="text-zinc-200">{detailDialog.animal_type || '-'}</span></div>
              <div><span className="text-[10px] text-zinc-600 block">Costo</span><span className="text-emerald-400 font-medium">{fmtMoney(detailDialog.total_cost || detailDialog.quoted_cost)}</span></div>
              <div><span className="text-[10px] text-zinc-600 block">Creado</span><span className="text-zinc-400 text-xs">{fmt(detailDialog.created_at)}</span></div>
              {detailDialog.tracking_number && <div><span className="text-[10px] text-zinc-600 block">Tracking</span><span className="text-zinc-200">{detailDialog.tracking_number}</span></div>}
            </div>
            {detailDialog.receipt_url && (
              <div className="mt-4">
                <span className="text-[10px] text-zinc-600 block mb-1">Comprobante de Pago</span>
                <a href={detailDialog.receipt_url} target="_blank" rel="noreferrer" className="text-purple-400 text-xs hover:underline">Ver comprobante</a>
              </div>
            )}
            <Button variant="outline" onClick={() => setDetailDialog(null)} className="mt-4 w-full border-white/10 text-zinc-400 h-9 text-xs">Cerrar</Button>
          </div>
        </div>
      )}
    </div>
  );
}
